/**
 * Allocation Module
 * 
 * Handles feature usage limits and quota management.
 * Tracks consumption and checks availability.
 * 
 * @package @appfactory/checkpoint-client
 * @version 1.0.0
 */

import { HttpClient } from './http-client';
import {
    AllocationStatus,
    ResourceAllocation,
    AllocationCheckResult,
    AllocationConsumeResult,
    AllocationConsumeOptions,
    CheckpointConfig,
} from './types';
 
/**
 * Allocation module for Checkpoint
 * 
 * Manages feature usage limits based on user tiers.
 * 
 * @example
 * ```typescript
 * const alloc = new CheckpointAlloc(httpClient, config);
 * 
 * // Check if user can use a feature
 * const { available, remaining } = await alloc.canUse('domains', 'email', 'send');
 * 
 * // Consume allocation
 * await alloc.consume('domains', 'email', 'send', 1);
 * 
 * // Get all allocations
 * const allocations = await alloc.getAll();
 * ```
 */
export class CheckpointAlloc {
    private http: HttpClient;
    private config: CheckpointConfig;
    private cachedAllocations: AllocationStatus | null = null;
    private cacheTtl: number = 30000; // 30 seconds
    private lastFetch: number = 0;

    constructor(http: HttpClient, config: CheckpointConfig) {
        this.http = http;
        this.config = config;
    }

    /**
     * Get all allocations for the current user
     * 
     * @param forceRefresh Force a refresh from server
     * @returns Full allocation status
     */
    async getAll(forceRefresh = false): Promise<AllocationStatus> {
        if (!forceRefresh && this.cachedAllocations && Date.now() - this.lastFetch < this.cacheTtl) {
            return this.cachedAllocations;
        }

        this.cachedAllocations = await this.http.get<AllocationStatus>('/alloc', true);
        this.lastFetch = Date.now();

        return this.cachedAllocations;
    }

    /**
     * Get allocation for a specific resource
     * 
     * @param category Category (e.g., 'domains', 'websites')
     * @param resource Resource (e.g., 'email', 'hosting')
     * @returns Resource allocation details
     */
    async get(category: string, resource: string): Promise<ResourceAllocation> {
        return this.http.get<ResourceAllocation>(`/alloc/${category}/${resource}`, true);
    }

    /**
     * Check if allocation is available
     * 
     * @param category Category
     * @param resource Resource
     * @param metric Metric to check (e.g., 'count', 'send')
     * @param amount Amount to check
     * @returns Availability result
     */
    async canUse(
        category: string,
        resource: string,
        metric: string,
        amount = 1
    ): Promise<AllocationCheckResult> {
        const result = await this.http.post<AllocationCheckResult>('/alloc/check', {
            category,
            resource,
            metric,
            amount,
        }, true);

        // Invalidate cache since we want fresh data after check
        this.invalidateCache();

        return result;
    }

    /**
     * Consume allocation
     * 
     * @param category Category
     * @param resource Resource
     * @param metric Metric
     * @param amount Amount to consume
     * @param options Consume options
     * @returns Consume result with remaining allocation
     */
    async consume(
        category: string,
        resource: string,
        metric: string,
        amount = 1,
        options?: AllocationConsumeOptions
    ): Promise<AllocationConsumeResult> {
        const result = await this.http.post<AllocationConsumeResult>('/alloc/consume', {
            category,
            resource,
            metric,
            amount,
            context: options?.context,
        }, true);

        // Invalidate cache after consumption
        this.invalidateCache();

        return result;
    }

    /**
     * Release allocation (decrement usage)
     * 
     * @param category Category
     * @param resource Resource
     * @param metric Metric
     * @param amount Amount to release
     * @param options Release options
     * @returns Release result
     */
    async release(
        category: string,
        resource: string,
        metric: string,
        amount = 1,
        options?: AllocationConsumeOptions
    ): Promise<AllocationConsumeResult> {
        const result = await this.http.post<AllocationConsumeResult>('/alloc/release', {
            category,
            resource,
            metric,
            amount,
            context: options?.context,
        }, true);

        // Invalidate cache after release
        this.invalidateCache();

        return result;
    }

    /**
     * Get remaining allocation for a specific metric
     * 
     * @param category Category
     * @param resource Resource
     * @param metric Metric
     * @returns Remaining amount (-1 for unlimited)
     */
    async getRemaining(category: string, resource: string, metric: string): Promise<number> {
        const allocation = await this.get(category, resource);
        const metricData = allocation.metrics[metric];

        if (!metricData) {
            return 0;
        }

        return metricData.remaining;
    }

    /**
     * Get limit for a specific metric
     * 
     * @param category Category
     * @param resource Resource
     * @param metric Metric
     * @returns Limit amount (-1 for unlimited)
     */
    async getLimit(category: string, resource: string, metric: string): Promise<number> {
        const allocation = await this.get(category, resource);
        const metricData = allocation.metrics[metric];

        if (!metricData) {
            return 0;
        }

        return metricData.limit;
    }

    /**
     * Get current usage for a specific metric
     * 
     * @param category Category
     * @param resource Resource
     * @param metric Metric
     * @returns Current usage amount
     */
    async getUsage(category: string, resource: string, metric: string): Promise<number> {
        const allocation = await this.get(category, resource);
        const metricData = allocation.metrics[metric];

        if (!metricData) {
            return 0;
        }

        return metricData.used;
    }

    /**
     * Check if a metric is unlimited
     * 
     * @param category Category
     * @param resource Resource
     * @param metric Metric
     * @returns True if unlimited
     */
    async isUnlimited(category: string, resource: string, metric: string): Promise<boolean> {
        const allocation = await this.get(category, resource);
        const metricData = allocation.metrics[metric];

        if (!metricData) {
            return false;
        }

        return metricData.unlimited;
    }

    /**
     * Get usage history
     * 
     * @param category Optional category filter
     * @param limit Number of records to return
     * @returns Usage history
     */
    async getHistory(
        category?: string,
        limit = 100
    ): Promise<Array<{
        category: string;
        resource: string;
        metric: string;
        amount: number;
        operation: 'consume' | 'release' | 'reset';
        context: Record<string, unknown> | null;
        created_at: string;
    }>> {
        const params = new URLSearchParams();
        if (category) params.set('category', category);
        params.set('limit', limit.toString());

        const result = await this.http.get<{ history: Array<{
            category: string;
            resource: string;
            metric: string;
            amount: number;
            operation: 'consume' | 'release' | 'reset';
            context: Record<string, unknown> | null;
            created_at: string;
        }> }>(`/alloc/history?${params.toString()}`, true);

        return result.history;
    }

    /**
     * Get percentage used for a metric
     * 
     * @param category Category
     * @param resource Resource
     * @param metric Metric
     * @returns Percentage used (0-100), or -1 for unlimited
     */
    async getPercentageUsed(category: string, resource: string, metric: string): Promise<number> {
        const allocation = await this.get(category, resource);
        const metricData = allocation.metrics[metric];

        if (!metricData || metricData.unlimited) {
            return -1;
        }

        if (metricData.limit === 0) {
            return 100;
        }

        return metricData.percentage_used ?? Math.round((metricData.used / metricData.limit) * 100);
    }

    /**
     * Clear the cache
     */
    clearCache(): void {
        this.cachedAllocations = null;
        this.lastFetch = 0;
    }

    /**
     * Invalidate cache (alias for clearCache)
     */
    private invalidateCache(): void {
        this.clearCache();
    }

    /**
     * Set cache TTL in milliseconds
     * 
     * @param ttl Cache TTL
     */
    setCacheTtl(ttl: number): void {
        this.cacheTtl = ttl;
    }
}
